import { mapInfoAnalyse } from './maps';

type MapName = keyof typeof mapInfoAnalyse;

/**
 * Checks if a round has a specific versus type situation (e.g., 4v5, 5v4, 4v4)
 * @param roundData The round data to check
 * @param side The side to check from ("T" or "CT")
 * @param versusType The versus type to check for ("4v5", "5v4", "4v4")
 * @returns True if the round has more than 10 frames matching the versus type
 */
export function roundHasVersusType(roundData: any, side: string, versusType: string): boolean {
  if (!roundData || !roundData.frames || roundData.frames.length === 0) {
    return false;
  }

  for (const frame of roundData.frames) {
    const sidePlayers = side === "T" ? frame.t.players : frame.ct.players;
    const opponentSidePlayers = side === "T" ? frame.ct.players : frame.t.players;
    
    const sideAliveCount = sidePlayers.filter((p: any) => p.hp > 0).length;
    const opponentAliveCount = opponentSidePlayers.filter((p: any) => p.hp > 0).length;
    
    let isMatchingFrame = false;
    
    switch (versusType) {
      case "4v5":
        isMatchingFrame = sideAliveCount === 4 && opponentAliveCount === 5;
        break;
      case "5v4":
        isMatchingFrame = sideAliveCount === 5 && opponentAliveCount === 4;
        break;
      case "4v4":
        isMatchingFrame = sideAliveCount === 4 && opponentAliveCount === 4;
        break;
      default:
        isMatchingFrame = false;
    }
    
    if (isMatchingFrame) {
      return true;
    }
  }
  
  return false;
}

/**
 * Calculates the distance between two points in 2D space
 */
function calculateDistance(x1: number, y1: number, z1: number, x2: number, y2: number, z2: number): number {
  return Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2) + Math.pow(z2 - z1, 2));
}

/**
 * Determines which bombsite (A or B) had more action in a round
 * @param roundData The round data to analyze
 * @param mapName The name of the map (must include 'de_' prefix)
 * @returns "A" or "B" based on which site had more action, or undefined if can't determine
 */
export function determineBombsite(roundData: any, mapName: string): "A" | "B" | undefined {
  if (!roundData || !mapName) {
    return undefined;
  }

  // Ensure mapName is a valid key
  const mapKey = mapName.startsWith('de_') ? mapName : `de_${mapName}`;
  if (!(mapKey in mapInfoAnalyse)) {
    return undefined;
  }

  const mapInfo = mapInfoAnalyse[mapKey as MapName];
  if (!mapInfo || !mapInfo.bombsiteA || !mapInfo.bombsiteB) {
    return undefined;
  }

  // First check if there was a bomb plant
  const bombPlant = roundData.bombEvents?.find((event: any) => event.bombAction === "plant");
  if (bombPlant) {
    const distanceToA = calculateDistance(bombPlant.playerX, bombPlant.playerY, bombPlant.playerZ, mapInfo.bombsiteA.x, mapInfo.bombsiteA.y, mapInfo.bombsiteA.z);
    const distanceToB = calculateDistance(bombPlant.playerX, bombPlant.playerY, bombPlant.playerZ, mapInfo.bombsiteB.x, mapInfo.bombsiteB.y, mapInfo.bombsiteB.z);
    return distanceToA < distanceToB ? "A" : "B";
  }

  // If no plant, analyze kills and final positions
  let aPoints = 0;
  let bPoints = 0;

  // Analyze kills
  if (roundData.kills) {
    for (const kill of roundData.kills) {
      const distanceToA = calculateDistance(kill.victimX, kill.victimY, kill.victimZ, mapInfo.bombsiteA.x, mapInfo.bombsiteA.y, mapInfo.bombsiteA.z);
      const distanceToB = calculateDistance(kill.victimX, kill.victimY, kill.victimZ, mapInfo.bombsiteB.x, mapInfo.bombsiteB.y, mapInfo.bombsiteB.z);
      
      if (distanceToA < distanceToB) {
        aPoints++;
      } else {
        bPoints++;
      }
    }
  }

  // Analyze final positions of alive players
  const lastFrame = roundData.frames[roundData.frames.length - 1];
  if (lastFrame) {
    const allPlayers = [...lastFrame.t.players, ...lastFrame.ct.players];
    for (const player of allPlayers) {
      if (player.hp > 0) { // Only count alive players
        const distanceToA = calculateDistance(player.x, player.y, player.z, mapInfo.bombsiteA.x, mapInfo.bombsiteA.y, mapInfo.bombsiteA.z);
        const distanceToB = calculateDistance(player.x, player.y, player.z, mapInfo.bombsiteB.x, mapInfo.bombsiteB.y, mapInfo.bombsiteB.z);
        
        if (distanceToA < distanceToB) {
          aPoints++;
        } else {
          bPoints++;
        }
      }
    }
  }

  // Return the site with more points, or undefined if equal
  if (aPoints === bPoints) {
    return undefined;
  }
  return aPoints > bPoints ? "A" : "B";
}
